import {
  ActionIcon,
  Button,
  Card,
  Flex,
  Group,
  Stack,
  Text,
  Title,
  useMantineColorScheme,
} from "@mantine/core";
import { IconPencil } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";

import { TextSerif } from "@/components/elements";
import { formatDate } from "@/utils/formatDate";
import { formatCurrency } from "@/utils/formatEuro";

import type { UpdateCarInput } from "@/services/cars/update-car";
import type { UseFormReturnType } from "@mantine/form";
import type { FC } from "react";

interface Props {
  form: UseFormReturnType<UpdateCarInput>;
  loading: boolean;
  onCancel?: () => void;
  onSubmit: () => void;
  activeStep: number;
  setActiveStep: (step: number) => void;
  nextStep: () => void;
}

const EditStep4: FC<Props> = ({ form, loading, onCancel, activeStep, setActiveStep, nextStep }) => {
  const { t } = useTranslation();
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === "dark";
  const cardBG = isDark ? "#222222" : "#f7f8fa";
  const labelColor = isDark ? "#a1a1aa" : "#6b7280";

  const renderRow = (label: string, value?: string | number | null) => (
    <Flex justify="space-between" align="center">
      <TextSerif fz="sm" c={labelColor}>
        {label}
      </TextSerif>
      <Text fz="sm" fw={500}>
        {value !== undefined && value !== null && value !== "" ? String(value) : "-"}
      </Text>
    </Flex>
  );

  return (
    <Stack gap="lg">
      <Title order={4}>{t("components.CreateCarForm.titles.reviewAndConfirm")}</Title>

      <Card bg={cardBG} withBorder radius="sm" p="lg">
        <Flex justify="space-between" align="center" mb="md">
          <Title order={5}>{t("components.CreateCarForm.basics")}</Title>
          <ActionIcon variant="light" onClick={() => setActiveStep(1)} aria-label="Edit basics">
            <IconPencil size={18} />
          </ActionIcon>
        </Flex>
        <Stack gap="md">
          {renderRow(t("car.carNickname"), form.values.nickname)}
          {renderRow(t("car.owner"), form.values.owner)}
          {renderRow(
            t("car.purchase_price"),
            form.values.purchase_price
              ? formatCurrency(Number(form.values.purchase_price), "EUR")
              : "-"
          )}
          {renderRow(
            t("car.purchase_date"),
            form.values.purchase_date ? formatDate(String(form.values.purchase_date)) : "-"
          )}
        </Stack>
      </Card>

      <Card bg={cardBG} withBorder radius="sm" p="lg">
        <Flex justify="space-between" align="center" mb="md">
          <Title order={5}>{t("components.CreateCarForm.carDetails")}</Title>
          <ActionIcon
            variant="light"
            onClick={() => setActiveStep(2)}
            aria-label="Edit car details"
          >
            <IconPencil size={18} />
          </ActionIcon>
        </Flex>
        <Stack gap="md">
          {renderRow(t("car.licence_plate"), form.values.licence_plate)}
          {renderRow(t("car.country_of_registration"), form.values.country_of_registration)}
          {renderRow(t("car.vin"), form.values.vin)}
          {renderRow(t("car.color"), form.values.color)}
          {renderRow(t("car.interior_colors"), form.values.interior_colors)}
          {renderRow(
            t("car.condition"),
            form.values.condition ? t(`car.conditions.${form.values.condition}`) : "-"
          )}
        </Stack>
      </Card>

      <Card bg={cardBG} withBorder radius="sm" p="lg">
        <Flex justify="space-between" align="center" mb="md">
          <Title order={5}>{t("components.CreateCarForm.extraDetails")}</Title>
          <ActionIcon
            variant="light"
            onClick={() => setActiveStep(3)}
            aria-label="Edit extra details"
          >
            <IconPencil size={18} />
          </ActionIcon>
        </Flex>
        <Stack gap="md">
          {renderRow(t("car.number_of_previous_owner"), form.values.number_of_previous_owner)}
          {renderRow(t("car.special_equipment"), form.values.special_equipment)}
        </Stack>
      </Card>

      <Group justify="space-between" mt="2em">
        <Text c="var(--mantine-color-primaryDark-5)" fw={500} fz="sm">
          {t("common.steps", { currentStep: activeStep + 1, totalSteps: 4 })}
        </Text>

        <Group mt="sm">
          {onCancel && (
            <Button variant="outline" onClick={onCancel} disabled={loading} c="dark">
              {t("common.cancel")}
            </Button>
          )}

          <Button type="button" onClick={nextStep} loading={loading}>
            {t("common.next")}
          </Button>
        </Group>
      </Group>
    </Stack>
  );
};

export default EditStep4;
